import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'
import { AppShell } from './components/layout/AppShell'
import { isBon8Staff } from './components/routing/RoleGate'
import { ProtectedRoute } from './components/routing/ProtectedRoute'
import { LoadingState } from './components/ui/LoadingState'
import { useCurrentUser } from './features/auth/hooks'
import { useClients } from './features/clients/hooks'
import { audienceLabel } from './features/clients/labels'
import { useClientStore } from './stores/clientStore'

const LoginPage = lazy(() => import('./pages/LoginPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ClientsListPage = lazy(() => import('./pages/ClientsListPage'))
const OverviewPage = lazy(() => import('./pages/OverviewPage'))
const MembersPage = lazy(() => import('./pages/MembersPage'))
const LeadPipelinePage = lazy(() => import('./pages/LeadPipelinePage'))
const CustomerJourneyDashboardPage = lazy(() => import('./pages/CustomerJourneyDashboardPage'))
const PatientsPage = lazy(() => import('./pages/PatientsPage'))
const VisitRecordsPage = lazy(() => import('./pages/VisitRecordsPage'))
const ClientSettingsPage = lazy(() => import('./pages/ClientSettingsPage'))
const SourcesPage = lazy(() => import('./pages/SourcesPage'))
const AdsDashboardPage = lazy(() => import('./pages/AdsDashboardPage'))
const AdsCampaignsPage = lazy(() => import('./pages/AdsCampaignsPage'))
const AdsPerformancePage = lazy(() => import('./pages/AdsPerformancePage'))
const EventsPage = lazy(() => import('./pages/EventsPage'))
const ClientPlaceholderPage = lazy(() => import('./pages/ClientPlaceholderPage'))
const LineTrackingTrackPage = lazy(() => import('./pages/LineTrackingTrackPage'))
const LineTrackingViewQRPage = lazy(() => import('./pages/LineTrackingViewQRPage'))
const LineTrackingPage = lazy(() => import('./pages/LineTrackingPage'))
const LineTrackingDetailPage = lazy(() => import('./pages/LineTrackingDetailPage'))
const LineUsersPage = lazy(() => import('./pages/LineUsersPage'))
const QrTrackingDashboardPage = lazy(() => import('./pages/QrTrackingDashboardPage'))
const TrackingListPage = lazy(() => import('./pages/TrackingListPage'))
const AgenticAIPage = lazy(() => import('./pages/AgenticAIPage'))
const StrategyCenterPage = lazy(() => import('./pages/StrategyCenterPage'))
const StrategistFlowPage = lazy(() => import('./pages/StrategistFlowPage'))
const SopInputsPage = lazy(() => import('./pages/SopInputsPage'))
const CampaignWorkbenchPage = lazy(() => import('./pages/CampaignWorkbenchPage'))
const EvaluationDashboardPage = lazy(() => import('./pages/EvaluationDashboardPage'))
const ContentCenterPage = lazy(() => import('./pages/ContentCenterPage'))
const CustomerProfilePage = lazy(() => import('./pages/CustomerProfilePage'))
const ChatCenterPage = lazy(() => import('./pages/ChatCenterPage'))
const TasksPage = lazy(() => import('./pages/TasksPage'))
const SearchPage = lazy(() => import('./pages/SearchPage'))
const SettingsPageNew = lazy(() => import('./pages/SettingsPage'))
const ReviewCenterPage = lazy(() => import('./pages/ReviewCenterPage'))
const ClientProfilePage = lazy(() => import('./pages/ClientProfilePage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

function LoadingFallback() {
  return <LoadingState fullPage />
}

function HomeRedirect() {
  const currentClientId = useClientStore((s) => s.currentClientId)
  const { data: user, isPending: userPending } = useCurrentUser()
  const { data: clients, isPending: clientsPending } = useClients()

  if (userPending || clientsPending) {
    return <LoadingFallback />
  }

  if (isBon8Staff(user?.platform_role)) {
    return <Navigate to="/dashboard" replace />
  }

  const preferredClient = clients?.find((client) => client.id === currentClientId) ?? clients?.[0]
  if (preferredClient) {
    return <Navigate to={`/clients/${preferredClient.id}`} replace />
  }

  return (
    <div className="font-330 text-body text-black/55">
      You do not have access to any client yet.
    </div>
  )
}

function CustomerJourneyPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const { data: clients } = useClients()
  const currentClient = clients?.find((item) => item.id === clientId)
  const label = audienceLabel(currentClient?.industry)

  return (
    <ClientPlaceholderPage
      eyebrow="Experience"
      title={`${label} Journey`}
      description={`A timeline view of how ${label.toLowerCase()}s move across touchpoints.`}
      emptyTitle={`${label} journey coming soon`}
      emptyDescription={`This page will map ${label.toLowerCase()} behavior from sources, events, segments, and campaign touchpoints.`}
    />
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Public LINE QR tracking — no auth, no AppShell (LINE_TRACKING_SPEC §2.1) */}
          {/* /line is the LIFF endpoint URL configured in LINE Dev Console; LINE
              appends ?aid={code} when opening line://app/{liffId}?aid=… */}
          <Route path="/line" element={<LineTrackingTrackPage />} />
          <Route path="/qr/:code" element={<LineTrackingViewQRPage />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<AppShell />}>
              <Route index element={<HomeRedirect />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/review-center" element={<ReviewCenterPage />} />
              <Route path="/clients" element={<ClientsListPage />} />
              <Route path="/clients/:clientId" element={<OverviewPage />} />
              <Route path="/clients/:clientId/customers" element={<PatientsPage />} />
              <Route path="/clients/:clientId/customers/:patientId" element={<CustomerProfilePage />} />
              <Route path="/clients/:clientId/events" element={<EventsPage />} />
              <Route path="/clients/:clientId/customer-journey" element={<CustomerJourneyPage />} />
              <Route
                path="/clients/:clientId/customer-journey/dashboard"
                element={<CustomerJourneyDashboardPage />}
              />
              <Route
                path="/clients/:clientId/customer-journey/lead-pipeline"
                element={<LeadPipelinePage />}
              />
              <Route
                path="/clients/:clientId/customer-journey/visit-records"
                element={<VisitRecordsPage />}
              />
              <Route path="/clients/:clientId/sources" element={<SourcesPage />} />
              <Route path="/clients/:clientId/ads" element={<Navigate to="dashboard" replace />} />
              <Route path="/clients/:clientId/ads/dashboard" element={<AdsDashboardPage />} />
              <Route path="/clients/:clientId/ads/campaigns" element={<AdsCampaignsPage />} />
              <Route path="/clients/:clientId/ads/performance" element={<AdsPerformancePage />} />
              <Route path="/clients/:clientId/agentic-ai" element={<AgenticAIPage />} />
              <Route path="/clients/:clientId/strategy" element={<StrategyCenterPage />} />
              <Route path="/clients/:clientId/strategist-flow" element={<StrategistFlowPage />} />
              <Route path="/clients/:clientId/sop-inputs" element={<SopInputsPage />} />
              <Route path="/clients/:clientId/campaigns" element={<CampaignWorkbenchPage />} />
              <Route path="/clients/:clientId/evaluation" element={<EvaluationDashboardPage />} />
              <Route path="/clients/:clientId/lead-center" element={<LeadPipelinePage />} />
              <Route path="/clients/:clientId/content-center" element={<ContentCenterPage />} />
              <Route path="/clients/:clientId/chat-center" element={<ChatCenterPage />} />
              <Route path="/clients/:clientId/tasks" element={<TasksPage />} />
              <Route path="/clients/:clientId/search" element={<SearchPage />} />
              <Route path="/clients/:clientId/qr-tracking" element={<QrTrackingDashboardPage />} />
              <Route
                path="/clients/:clientId/qr-tracking/line-tracking-list"
                element={<TrackingListPage />}
              />
              <Route path="/clients/:clientId/qr-tracking/line-users" element={<LineUsersPage />} />
              <Route path="/clients/:clientId/qr-tracking/oa-accounts" element={<LineTrackingPage />} />
              <Route
                path="/clients/:clientId/qr-tracking/oa-accounts/:qrId"
                element={<LineTrackingDetailPage />}
              />
              <Route path="/clients/:clientId/profile" element={<ClientProfilePage />} />
              <Route path="/clients/:clientId/settings" element={<SettingsPageNew />} />
              <Route
                path="/clients/:clientId/settings/client"
                element={<ClientSettingsPage />}
              />
              <Route path="/clients/:clientId/members" element={<MembersPage />} />
            </Route>
          </Route>

          {/* Short-code landing — must come after all static routes (LINE_TRACKING_SPEC §2.1) */}
          <Route path="/:code" element={<LineTrackingTrackPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
